import React, { useState, useEffect } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar, { MenuSection } from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false); // Only used for mobile

  // Determine selected section from URL
  const getSelectedSection = (): MenuSection => {
    if (location.pathname.startsWith('/diary')) return 'diary';
    if (location.pathname.startsWith('/journal')) return 'journals';
    if (location.pathname.startsWith('/download')) return 'download';
    return 'thoughts';
  };

  const [selectedSection, setSelectedSection] = useState<MenuSection>(getSelectedSection());

  useEffect(() => {
    setSelectedSection(getSelectedSection());
  }, [location.pathname]);

  const handleSectionSelect = (section: MenuSection) => {
    setSelectedSection(section);
    if (section === 'thoughts') navigate('/');
    else if (section === 'diary') navigate('/diary');
    else if (section === 'journals') navigate('/journals');
    else if (section === 'download') navigate('/download');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Header onMenuClick={toggleSidebar} showMenuButton={isMobile} />
      <Sidebar
        open={sidebarOpen}
        selectedSection={selectedSection}
        onSectionSelect={handleSectionSelect}
        onClose={() => setSidebarOpen(false)}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          // On desktop, sidebar is permanent and takes up space, so no margin needed
          // On mobile, sidebar is temporary overlay, so no margin needed
          ml: 0,
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
